<template>
    <v-app>
        <v-app-bar app :class="classMenu" :style="cssVars">
            <v-btn
                @click="goBack"
                :color="$vuetify.theme.contrast.color"
                icon
            >
                <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-spacer />
            <v-img max-width="90" :src="getLogo" />
            <v-spacer />
        </v-app-bar>

        <v-content class="status-page">
            <!-- LOADING -->
            <v-container v-if="loading" class="fill-height">
                <v-row align="center" justify="center" class="text-center">
                    <div>
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="64"
                            width="5"
                        />
                        <div class="mt-4 font-weight-medium">
                            {{ $t('status.carregando') }}
                        </div>
                    </div>
                </v-row>
            </v-container>

            <!-- PEDIDO NÃO ENCONTRADO -->
            <v-container v-else-if="pedidoNaoEncontrado" class="fill-height">
                <v-row align="center" justify="center">
                    <v-col cols="12" sm="8" md="6">
                        <v-card class="pa-6 text-center elevation-2">
                            <v-icon size="64" color="grey">mdi-alert-circle-outline</v-icon>
                            <h2 class="mt-4">{{ mensagemErro }}</h2>
                            <p class="mt-2 grey--text">
                                {{ $t('status.verifiqueLink') }}
                            </p>
                            <v-btn color="primary" class="mt-4" @click="goBack">
                                {{ $t('status.voltarInicio') }}
                            </v-btn>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>

            <!-- CONTEÚDO NORMAL -->
            <v-container v-else class="py-6">
                <v-row justify="center">
                    <v-col cols="12" md="8" lg="6">
                        <v-card class="elevation-2 status-card">
                            <!-- CABEÇALHO -->
                            <v-card-text class="pb-2">
                                <div class="d-flex align-center justify-space-between flex-wrap">
                                    <div>
                                        <div class="caption grey--text text--darken-1">{{ $t('status.pedido') }}</div>
                                        <div class="title font-weight-bold">#{{ pedido.id }}</div>
                                    </div>
                                    <div class="text-right">
                                        <v-chip
                                            :color="statusColor"
                                            text-color="white"
                                            class="mb-2"
                                        >
                                            {{ statusLabel }}
                                        </v-chip>
                                        <div class="caption grey--text">
                                            {{ $t('status.realizadoEm', { data: createdAt }) }}
                                        </div>
                                    </div>
                                </div>
                            </v-card-text>

                            <!-- ALERTA DE CANCELAMENTO -->
                            <v-alert
                                v-if="pedido.status === 'cancelado'"
                                type="error"
                                border="left"
                                colored-border
                                prominent
                                class="mx-4 mt-2"
                            >
                                <div class="d-flex align-center">
                                    <div>
                                        <div class="font-weight-bold mb-1">
                                            {{ $t('status.cancelado') }}
                                        </div>
                                        <div class="text--secondary">
                                            <!-- Se o pagamento foi realizado, o estorno será processado em breve. -->
                                        </div>
                                    </div>
                                </div>
                            </v-alert>

                            <!-- STATUS / PREVISÃO -->
                            <v-divider v-if="pedido.status !== 'cancelado'" />

                            <v-card-text
                                v-if="pedido.status !== 'cancelado'"
                                class="py-4 text-center"
                            >
                                <div class="subtitle-2 font-weight-bold mb-1">
                                    {{ pedido.status === 'concluido' ? $t('status.finalizado') : $t('status.previsao') }}
                                </div>

                                <div
                                    v-if="pedido.status !== 'concluido' && horaFinalizacao"
                                    class="display-1 font-weight-bold"
                                >
                                    {{ horaFinalizacao }}
                                </div>

                                <div
                                    v-else-if="pedido.status === 'concluido'"
                                    class="display-1 font-weight-bold success--text"
                                >
                                    <v-icon size="40" color="success">mdi-check-circle</v-icon> {{ $t('status.pronto') }}
                                </div>

                                <div v-else class="caption grey--text">
                                    {{ $t('status.aguardandoTempo') }}
                                </div>

                                <div class="mt-2 caption grey--text">
                                    {{ $t('status.acompanheAbaixo') }}
                                    <br><i18n path="status.toqueAtualizar" :tag="false">
                                        <template #botao><strong>“{{ $t('status.atualizarStatus') }}”</strong></template>
                                    </i18n>
                                </div>
                            </v-card-text>

                            <!-- STEPPER -->
                            <v-divider v-if="pedido.status !== 'cancelado'" />
                            <v-card-text
                                v-if="pedido.status !== 'cancelado'"
                                class="py-4"
                            >
                                <v-stepper
                                    v-model="step"
                                    vertical
                                    elevation="0"
                                >
                                    <v-stepper-step
                                        :complete="step > 1"
                                        step="1"
                                        :class="{ 'stepper-loading': isAnimatingStepper && step === 1 }"
                                    >
                                        {{ $t('status.passoRealizado') }}
                                        <small class="mt-1">{{ createdAt }}</small>
                                    </v-stepper-step>
                                    <v-stepper-content step="1" />

                                    <v-stepper-step
                                        :complete="step > 2"
                                        step="2"
                                        :class="{ 'stepper-loading': isAnimatingStepper && step === 2 }"
                                    >
                                        {{ $t('status.passoPreparacao') }}
                                        <small v-if="step > 2 && preparacaoAt" class="mt-1">
                                            {{ $t('status.entrouNaFila', { hora: preparacaoAt }) }}
                                        </small>
                                    </v-stepper-step>
                                    <v-stepper-content step="2" />

                                    <v-stepper-step
                                        v-if="isDelivery && step >= 3"
                                        :complete="step > 3"
                                        step="3"
                                        :class="{ 'stepper-loading': isAnimatingStepper && step === 3 }"
                                    >
                                        {{ $t('status.passoSaiu') }}
                                        <small class="mt-1">{{ $t('status.aCaminho') }}</small>
                                    </v-stepper-step>
                                    <v-stepper-content v-if="isDelivery" step="3" />

                                    <v-stepper-step
                                        v-if="!isDelivery"
                                        :complete="step > 3"
                                        step="3"
                                        :class="{ 'stepper-loading': isAnimatingStepper && step === 3 }"
                                    >
                                        {{ $t('status.passoAguardandoRetirada') }}
                                        <small class="mt-1">
                                            {{ $t('status.retirarNoBalcao') }}
                                        </small>
                                    </v-stepper-step>
                                    <v-stepper-content v-if="!isDelivery" step="3" />

                                    <v-stepper-step
                                        step="4" :complete="step === 4"
                                         :class="{ 'stepper-loading': isAnimatingStepper && step === 4 }"
                                    >
                                        {{ $t('status.passoConcluido') }}
                                        <small v-if="step === 4" class="mt-1">
                                            {{ $t('status.foiConcluido') }}
                                        </small>
                                    </v-stepper-step>
                                    <v-stepper-content step="4" />
                                </v-stepper>

                                <v-btn
                                    color="primary"
                                    outlined
                                    class="mr-sm-2 mb-2"
                                    @click="refreshPedido"
                                    block
                                >
                                    <v-icon left>mdi-refresh</v-icon>
                                    {{ $t('status.atualizarStatus') }}
                                </v-btn>
                                <div class="caption grey--text">
                                    {{ $t('status.ultimaAtualizacao', { hora: ultimaAtualizacaoExibida }) }}
                                </div>
                            </v-card-text>

                            <!-- RESUMO DO PEDIDO -->
                            <v-divider />
                            <v-card-text class="py-4">
                                <div class="mb-3">
                                    <div class="caption grey--text text--darken-1">
                                        {{ $t('status.cliente') }}
                                    </div>
                                    <div class="font-weight-medium">
                                        {{ pedido.cliente?.nome || '—' }}
                                    </div>
                                </div>

                                <v-row>
                                    <v-col cols="12" sm="6" class="mb-3">
                                        <div class="caption grey--text text--darken-1">
                                            {{ $t('status.formaEntrega') }}
                                        </div>
                                        <div class="font-weight-medium">
                                            {{ entregaLabel }}
                                        </div>
                                    </v-col>

                                    <v-col cols="12" sm="6" class="mb-3">
                                        <div class="caption grey--text text--darken-1">
                                            {{ $t('status.formaPagamento') }}
                                        </div>
                                        <div class="font-weight-medium">
                                            {{ pagamentoLabel }}
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-row class="align-center mt-1">
                                    <v-col cols="12" sm="6" class="mb-3">
                                        <div class="caption grey--text text--darken-1">
                                            {{ $t('status.total') }}
                                        </div>
                                        <div class="display-1 font-weight-bold primary--text">
                                            {{ formatCurrency(pedido.total) }}
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <Footer />
            </v-container>
        </v-content>
    </v-app>
</template>

<script>
import Footer from '@/components/Footer';
import { mapActions } from 'vuex';
import { formatarMoeda, formatoLegado } from '@/utils/moeda';

export default {
    name: 'PedidoStatus',

    components: { Footer },

    data: () => ({
        step: 1,
        pedido: {},
        configuracoes: {},
        pedidoNaoEncontrado: false,
        mensagemErro: '',
        loading: false,
        ultimaAtualizacao: '',
        formasPagamento: [],
    }),

    mounted() {
        this.getPedido();
        this.getConfiguracoes();
    },

    computed: {
        statusColor() {
            const map = {
                cancelado: 'error',
                concluido: 'success',
                'em_preparacao': 'primary',
                'aguardando_retirada': 'warning',
                'saiu_para_entrega': 'info',
                'confirmado': 'info',
            };
            return map[this.pedido.status] || 'grey';
        },

        createdAt() {
            if (!this.pedido.created_at) return '';
            return this.moment(this.pedido.created_at).format(this.$t('status.formatoCriadoEm'));
        },

        formatoHora() {
            return this.$t('status.formatoHora');
        },

        // Formatada na exibição: a configuração (e o idioma) da loja pode chegar depois do pedido.
        ultimaAtualizacaoExibida() {
            return this.ultimaAtualizacao
                ? this.moment(this.ultimaAtualizacao).format(this.$t('status.formatoHoraSegundos'))
                : '';
        },

        horaFinalizacao() {
            const intervalo = this.isDelivery
                ? this.configuracoes.tempo_entrega
                : this.configuracoes.tempo_retirada;

            if (!intervalo || !this.pedido.prazo_finalizacao) return '';

            const [min, max] = intervalo.split(' - ').map(Number);

            if (isNaN(min) || isNaN(max)) {
                return this.formatDate(this.pedido.prazo_finalizacao, this.formatoHora);
            }

            const diff = max - min;
            const prazoDe = this.moment(this.pedido.prazo_finalizacao)
                .subtract(diff, 'minutes')
                .format(this.formatoHora);
            const prazoAte = this.formatDate(this.pedido.prazo_finalizacao, this.formatoHora);

            return `${prazoDe} - ${prazoAte}`;
        },

        isDelivery() {
            return this.pedido.entrega === 2;
        },

        entregaLabel() {
            return [1, 2, 3].includes(this.pedido.entrega)
                ? this.$t('status.entrega.' + this.pedido.entrega)
                : '—';
        },

        pagamentoLabel() {
            const formaPagamento = this.formasPagamento.find(e => e.id == this.pedido.forma_pagamento);
            return formaPagamento
                ? (this.tSistema('sistema.formaPagamento.' + formaPagamento.id, formaPagamento.descricao) || '—')
                : '—';
        },

        statusLabel() {
            const s = this.pedido.status;

            return this.$te('status.rotulos.' + s) ? this.$t('status.rotulos.' + s) : (s || '—');
        },

        // A hora de cada status é gravada em Brasília: fora do Brasil, mostrada no fuso da loja.
        preparacaoAt() {
            const historico = this.pedido.status_historico || [];
            const status = historico.find(e => e.status === 'em_preparacao');
            if (!status || !status.updated_at) return '';
            return this.formatInstante(status.updated_at, this.formatoHora);
        },

        classMenu() {
            return this.$vuetify.theme.isDark ? 'app-bar-dark' : 'app-bar-light';
        },

        cssVars() {
            return {
                '--bg-color': this.$vuetify.theme.custom.appBarColor,
                padding: '0 0 10px 0',
            };
        },

        getLogo() {
            const theme = this.$vuetify.theme.contrast.theme || 'dark';
            const logo = require(`@/assets/zapermenu-${theme}.png`);
            return logo;
        },

        isAnimatingStepper() {
            const status = this.pedido.status;
            return !['concluido', 'cancelado'].includes(status);
        },
    },

    methods: {
        ...mapActions(['setAppData']),

        formatCurrency(value) {
            if (!value && value !== 0) return '—';

            if (!formatoLegado()) {
                return formatarMoeda(value);
            }

            return Number(value).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            });
        },

        marcarUltimaAtualizacao() {
            this.ultimaAtualizacao = Date.now();
        },

        refreshPedido() {
            this.getPedido();
        },

        getPedido() {
            this.loading = true;
            this.pedidoNaoEncontrado = false;
            this.mensagemErro = '';
            this.$http
                .get(`pedido/${this.$route.params.pedUuid}`)
                .then(resp => {
                    // A mensagem da API pode vir traduzida: o que vale é não ter pedido.
                    if (!resp.data?.data?.pedido) {
                        this.pedidoNaoEncontrado = true;
                        this.mensagemErro = this.$t('status.naoEncontrado');
                        return;
                    }
                    this.pedido = resp.data.data.pedido;
                    this.setStep();
                    this.marcarUltimaAtualizacao();
                })
                .catch(() => {
                    this.pedidoNaoEncontrado = true;
                    this.mensagemErro = this.$t('status.naoConcluiu');
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        setStep() {
            if (!this.pedido || !this.pedido.status) return;
            if (this.pedido.status === 'em_preparacao') this.step = 2;
            if (['aguardando_retirada', 'saiu_para_entrega'].includes(this.pedido.status)) this.step = 3;
            if (this.pedido.status === 'concluido') this.step = 4;
        },

        getConfiguracoes() {
            this.$http
                .get('configuracoes')
                .then(resp => {
                    this.configuracoes = resp.data.data;
                    this.formasPagamento = resp.data.data.formas_pagamento || [];
                    this.setAppData({ configuracoes: this.configuracoes });
                })
                .catch(() => {
                    this.notify && this.notify(this.$t('status.naoConcluiuAviso'), 'warning');
                });
        },

        goBack() {
            this.$router.push(`/${this.$route.params.emp}`);
            location.reload();
        },
    },
};
</script>

<style scoped>
.status-page {
    background: linear-gradient(180deg, #fafafa 0%, #f1f1f1 60%, #e9e9e9 100%);
    min-height: 100vh;
}

/* No tema escuro o texto do Vuetify é branco: o fundo segue o do app. */
.theme--dark .status-page {
    background: transparent;
}

.status-card {
    margin-bottom: 40px;
    border-radius: 18px;
    overflow: hidden;
}

.app-bar-dark {
    background-color: var(--bg-color) !important;
}

.app-bar-light {
    background-color: var(--bg-color) !important;
}

@media (max-width: 600px) {
    .status-card {
        border-radius: 0;
        box-shadow: none;
    }
}

.stepper-loading::before {
    content: '';
    position: absolute;
    left: 35px;
    top: 55px;
    width: 2px;
    height: 40px;
    border-radius: 2px;

    background: linear-gradient(
        180deg,
        rgba(76, 175, 80, 0.8),
        rgba(76, 175, 80, 0.1),
        rgba(76, 175, 80, 0.8)
    );
    background-size: 100% 200%;
    animation: pulseLineDown 1.4s linear infinite;
}

@keyframes pulseLineDown {
    0% {
        background-position: 0% 200%;
    }
    100% {
        background-position: 0% 0%;
    }
}
</style>
